/**
 * ROTATION VIEW (2026-10-22, moved out of sections/cc_configurator unchanged) - the gallery slide that turns the model
 * through 24 angles: drag, flick or wheel the tick bar under it. Runs where the slide is (.cc-rotation-slide with
 * #cc-rotation-bar, #cc-rotation-bar-track, #cc-rotation-img and the 24 URLs in #cc-rotation-urls); the preset Set and
 * Collection pages still carry their own copy.
 */
/*
  Rotation gallery slide — drag the bar to spin through 24 views
  Markup is rendered once in Liquid (24 ticks). At init we clone the
  row twice more — total = 72 ticks in the DOM. The user starts
  scrolled into the MIDDLE copy, and any scroll that enters the
  first or third copy gets warped back to the equivalent middle
  position so the bar feels infinite
  No backend polling — all 24 URLs are inlined as JSON beside the
  slide. We keep two sizes (preview/full) so the magnifier modal,
  which reads `data-img-full`, picks up the high-res view of the
  currently-shown angle
*/
(() => {
  function init() {
    const slide       = document.querySelector('.cc-rotation-slide');
    const rotation    = document.getElementById('cc-rotation-bar');
    const rotTrack    = document.getElementById('cc-rotation-bar-track');
    const previewImg  = document.getElementById('cc-rotation-img');
    const urlsScript  = document.getElementById('cc-rotation-urls');
    /*
      Background-fallback layer (see .cc-rotation-bg CSS): the previous
      URL gets snapshot here just before every src swap so the previous
      frame stays painted during the visible <img>'s decode gap.
    */
    const bgEl        = document.getElementById('cc-rotation-bg');
    if (!slide || !rotation || !rotTrack || !previewImg || !urlsScript) return;

    let smallUrls  = [];
    let mediumUrls = [];
    let fullUrls   = [];
    try {
      const data = JSON.parse(urlsScript.textContent || '{}');
      smallUrls  = Array.isArray(data.small)  ? data.small  : [];
      mediumUrls = Array.isArray(data.medium) ? data.medium : [];
      fullUrls   = Array.isArray(data.full)   ? data.full   : [];
    } catch (_) { return; }
    const TOTAL_VIEWS = 24;
    if (smallUrls.length !== TOTAL_VIEWS) return;

    const TICK_WIDTH = 24; /* matches `.rotation-bar__tick { flex: 0 0 24px }` */
    let currentViewIndex = 0;

    /*
      Multi-tier preload state
      smallLoaded[i]  : 400px-wide JPG for view i is in cache
      mediumLoaded[i] : 800px-wide JPG for view i is in cache
      fullLoaded[i]   : 2048px-wide JPG for view i is in cache
      (used by the magnifier modal — pickBestUrlForView
      always exposes the full URL on data-img-full,
      but the browser doesn't need it in cache until
      the user actually zooms.)
      Strategy:
      1. Init —
      • kick off all 24 smalls in parallel (always)
      • kick off view 0's medium so the visible <img> upgrades to
      800px without waiting for user interaction
      • warm view 0's full (2048) once the page has loaded, so the
      first zoom is sharp the moment the magnifier opens
      2. First user interaction (pointerdown on bar OR tick click) —
      kick off all 24 mediums in parallel
      3. Every view change — eager-fetch mediums for current + 2
      neighbours on both sides (overlaps with the bulk preload but
      ensures the angles the user is actually scrubbing through get
      the highest priority slots in the browser's connection pool)
      4. Display rule — show the medium if it's loaded for the current
      view, otherwise the small. data-img-full always points to the
      2048 URL whether it's cached or not; the magnifier modal will
      fetch on demand for any angle the user hasn't dwelt on yet
    */
    const smallLoaded  = new Array(TOTAL_VIEWS).fill(false);
    const mediumLoaded = new Array(TOTAL_VIEWS).fill(false);
    const fullLoaded   = new Array(TOTAL_VIEWS).fill(false);
    let mediumPreloadStarted = false;

    /*
      The Liquid-rendered <img> renders the small for view 0 as its
      initial src — mark it loaded immediately so the neighbour-upgrade
      logic can skip it when computing what to fetch.
    */
    smallLoaded[0] = true;

    /*
      Warm the OTHER 23 small-tier views so scrubbing feels instant — but
      OFF the critical path. Firing all 24 preloads at boot floods the
      ~6-connection-per-host pool and competes with the page's own
      load-blocking resources (the eager featured image, fonts, …), which
      on desktop pushed the load event — and the moment any view is ready
      to display — out to 10-15s. View 0 is already inline (eager) and its
      medium is fetched high-priority below; the rest can warm in the
      background once the page has loaded / gone idle, or the instant the
      user first interacts (whichever comes first).
    */
    let smallsWarmed = false;
    function warmRemainingSmalls() {
      if (smallsWarmed) return;
      smallsWarmed = true;
      smallUrls.forEach((url, idx) => {
        if (!url || idx === 0 || smallLoaded[idx]) return;
        const img = new Image();
        img.decoding = 'async';
        /*
          Ahead of the option-modal pictures, which warm at the same moment
          (cf-deferred-media.js): the rotation is on screen, the modals are not.
        */
        if ('fetchPriority' in img) img.fetchPriority = 'high';
        img.onload = () => { smallLoaded[idx] = true; };
        img.src = url;
      });
    }
    function scheduleSmallWarm() {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(warmRemainingSmalls, { timeout: 2000 });
      } else {
        setTimeout(warmRemainingSmalls, 200);
      }
    }
    if (document.readyState === 'complete') {
      scheduleSmallWarm();
    } else {
      window.addEventListener('load', scheduleSmallWarm, { once: true });
    }

    /*
      Reveal the visible <img> as soon as its current src has
      decoded. The previous approaches (inline `onload="…"` and a bare
      addEventListener) both showed the grey stage indefinitely on some
      paints — the load event was either missed (raced past the handler
      attachment) or never fired because the browser was already done
      loading before JS booted. This version is paranoid: every signal
      we can think of is wired up
    */
    function markRotationImgLoaded() {
      if (previewImg.dataset.loaded === 'true') return;
      previewImg.dataset.loaded = 'true';
    }
    /*
      Signal 1: already-complete check. Cached/cold images that landed
      before init() can be revealed immediately.
    */
    if (previewImg.complete && previewImg.naturalWidth > 0) {
      markRotationImgLoaded();
    }
    /*
      Signal 2: standard load/error event listeners. `once: false` on
      load because subsequent swapPreviewSrc calls will re-fire the
      event and we want every fresh URL to count as "loaded" too.
    */
    previewImg.addEventListener('load',  markRotationImgLoaded, { once: false });
    previewImg.addEventListener('error', markRotationImgLoaded, { once: true });
    /*
      Signal 3: JS-driven preload of view 0. If the inline <img>'s own
      fetch silently fails or the load event never fires, a parallel
      Image() request gives us a second chance — and once it has
      decoded, we mirror its src onto the visible <img> so we know a
      valid bitmap is sitting in cache.
    */
    {
      const firstUrl = smallUrls[0];
      if (firstUrl) {
        const pre = new Image();
        pre.decoding = 'async';
        pre.onload = () => {
          smallLoaded[0] = true;
          /*
            Only mirror the small onto the visible <img> if it has no
            rendered bitmap yet (the inline load genuinely failed). Never
            clobber an already-applied higher-res — a late-firing version
            of this used to bounce view 0 back to low-res *after* the init
            medium upgrade had landed.
          */
          if (previewImg.naturalWidth === 0 && previewImg.src !== pre.src) {
            previewImg.src = pre.src;
          }
          markRotationImgLoaded();
        };
        pre.onerror = markRotationImgLoaded;
        pre.src = firstUrl;
        if (pre.complete && pre.naturalWidth > 0) {
          smallLoaded[0] = true;
          markRotationImgLoaded();
        }
      } else {
        markRotationImgLoaded();
      }
    }
    /*
      Signal 4: timeout safety net. If none of the above fire within
      3s — broken URL, blocked request, exotic browser bug — we lift
      the visibility hide anyway. Better to show whatever the <img>
      is rendering (empty box, broken-image icon, whatever) than to
      stay stuck on a grey rectangle indefinitely.
    */
    setTimeout(markRotationImgLoaded, 3000);

    /*
      Shared preload that runs `img.decode()` after the fetch lands.
      Why: the visible <img>.src = url path needs a decoded bitmap to
      paint without a brief blank frame. The HTTP cache hands the bytes
      back instantly on subsequent loads, but the browser still has to
      re-decode them inside the visible <img> — and that decode is what
      caused the "fade away and come back" flicker on the first scrub
      through each angle. Awaiting decode() on the preload Image() warms
      the browser's *decoded* bitmap cache, so by the time setActiveView
      swaps the visible src the bitmap is already ready and the paint
      is glitch-free.
    */
    function preloadMedium(idx, highPriority) {
      if (mediumLoaded[idx]) return;
      const url = mediumUrls[idx];
      if (!url) return;
      const img = new Image();
      img.decoding = 'async';
      /*
        On mobile the visible angle's medium can otherwise sit behind 20+
        low-priority preloads (and the heavy full) and not arrive until the
        user first scrubs — bump the angle we're parked on to high.
      */
      if (highPriority && 'fetchPriority' in img) img.fetchPriority = 'high';
      img.src = url;
      const decodePromise = (typeof img.decode === 'function')
        ? img.decode().catch(() => {})
        : new Promise(resolve => {
            img.onload  = resolve;
            img.onerror = resolve;
          });
      decodePromise.then(() => {
        mediumLoaded[idx] = true;
        /*
          Upgrade-if-active: if the user is sitting on this angle right
          now, swap the visible <img> to the freshly-decoded medium.

          The SECOND argument is the full, not the medium again. It used to
          pass `url` twice, which wrote the 800 into data-img-full — and since
          view 0's medium decodes on every page load, the magnifier opened the
          800 rather than the 2048 on every visit, then zoomed 3.5x into it.
          What the <img> shows and what the modal opens are two different
          tiers, and only the first one is this URL.
        */
        if (idx === currentViewIndex && previewImg.src !== url) {
          swapPreviewSrc(url, fullUrls[idx] || url);
        }
        /*
          View 0: warm its full (2048) for the magnifier, but only once the
          page has loaded (cf-deferred-media.js). At 140 KB it used to go out
          high-priority the moment this medium landed, competing with the
          first screen; the magnifier fetches it on demand if opened sooner.
        */
        if (idx === 0) (window.cfWhenWarm || ((fn) => fn()))(() => preloadFull(0));
      });
    }

    function startMediumBulkPreload() {
      /*
        First interaction also warms any small-tier views still deferred
        from the post-load schedule, so a user who scrubs immediately gets
        every angle fetching right away.
      */
      warmRemainingSmalls();
      if (mediumPreloadStarted) return;
      mediumPreloadStarted = true;
      for (let i = 0; i < TOTAL_VIEWS; i++) preloadMedium(i);
    }

    function ensureMediumForNeighbourhood(centerIdx) {
      /* current + 2 ticks on each side (5 angles in total, wrapping). */
      for (let delta = -2; delta <= 2; delta++) {
        const idx = ((centerIdx + delta) % TOTAL_VIEWS + TOTAL_VIEWS) % TOTAL_VIEWS;
        preloadMedium(idx);
      }
    }

    /*
      Full-resolution (2048) preload. Used only at init for view 0 right
      now — bandwidth would be steep if we fetched all 24 fulls (~24 ×
      ~400 KB ≈ 10 MB), so the other angles' full URLs are surfaced on
      data-img-full but only fetched by the magnifier on demand. View 0
      gets a head-start because it's the most likely angle the user will
      zoom into first.
    */
    function preloadFull(idx, highPriority) {
      if (fullLoaded[idx]) return;
      const url = fullUrls[idx];
      if (!url) return;
      const img = new Image();
      img.decoding = 'async';
      if (highPriority && 'fetchPriority' in img) img.fetchPriority = 'high';
      img.src = url;
      const decodePromise = (typeof img.decode === 'function')
        ? img.decode().catch(() => {})
        : new Promise(resolve => {
            img.onload  = resolve;
            img.onerror = resolve;
          });
      decodePromise.then(() => { fullLoaded[idx] = true; });
    }

    /*
      Auto-upgrade view 0 to medium on initial load — even before the
      user touches the rotation tool — at HIGH priority so it actually
      arrives promptly on mobile instead of waiting for the first scrub.
      The user shouldn't have to scrub just to see the first angle sharp.
      (View 0's full is warmed from inside preloadMedium once the medium
      lands, so the heavy 2048 can't starve this fetch.)
    */
    preloadMedium(0, true);

    function pickBestUrlForView(idx) {
      /*
        Prefer medium if it's already in cache; otherwise fall back to
        the small if THAT's in cache. If neither is loaded yet, return
        `null` — setActiveView will skip the swap entirely, keeping the
        last cached angle on screen until the new one is ready. This is
        what kills the fast-drag flicker: we never assign an unfetched
        URL to the visible <img>.

        `full` (consumed by the magnifier modal via data-img-full) always
        points at the 2048 URL — whether or not it's been preloaded yet.
        For view 0 the bitmap is warmed once the page has loaded (preloadFull above);
        every other angle gets fetched on demand the first time the user
        zooms into it, which is acceptable for a click-driven action.
      */
      const fullUrl = fullUrls[idx] || mediumUrls[idx] || smallUrls[idx];
      if (mediumLoaded[idx] && mediumUrls[idx]) {
        return { url: mediumUrls[idx], full: fullUrl };
      }
      if (smallLoaded[idx] && smallUrls[idx]) {
        return { url: smallUrls[idx], full: fullUrl };
      }
      return null;
    }

    /*
      Clone the 24 ticks twice more (24 → 72). Mark the original as the
      "middle" copy; the user's scroll position lives here at rest.
    */
    const originals = Array.from(rotTrack.querySelectorAll('.rotation-bar__tick'));
    if (originals.length !== TOTAL_VIEWS) return;
    originals.forEach(t => t.dataset.copy = '1');
    const head = document.createDocumentFragment();
    const tail = document.createDocumentFragment();
    originals.forEach(t => {
      const a = t.cloneNode(true); a.dataset.copy = '0'; a.classList.remove('is-active');
      const b = t.cloneNode(true); b.dataset.copy = '2'; b.classList.remove('is-active');
      head.appendChild(a);
      tail.appendChild(b);
    });
    rotTrack.insertBefore(head, originals[0]);
    rotTrack.appendChild(tail);

    /* Click-to-jump on any tick. */
    rotTrack.querySelectorAll('.rotation-bar__tick').forEach(tick => {
      tick.addEventListener('click', (e) => {
        e.preventDefault();
        const idx = Number(tick.dataset.viewIndex);
        if (!Number.isFinite(idx)) return;
        startMediumBulkPreload();
        scrollToTickInMiddleCopy(idx, true);
      });
    });

    /*
      Motion: transform-driven (no native scrollLeft)
      The track is rendered at translateX(-rotOffset); rotOffset is the px
      equivalent of the old scrollLeft. The endless loop is a seamless modulo
      of rotOffset — the 3 copies are pixel-identical, so the wrap shows the
      same pixels (no scrollLeft teleport flicker). Ported from cc_custom
    */
    let rotOffset       = 0;
    let dragPointerId   = null;
    let dragStartX      = 0;
    let dragStartOffset = 0;
    let dragMoved       = false;
    let pendingOffset   = null;
    let pendingRaf      = null;
    let inertiaRaf      = null;
    let tweenRaf        = null;
    let wheelSnapT      = null;
    let scrubIdleT      = null;
    const velSamples = [];
    const VEL_WINDOW_MS = 80;

    const copyWidth = () => TOTAL_VIEWS * TICK_WIDTH;
    const viewportW = () => rotation.clientWidth;

    function markScrubbing() {
      rotation.classList.add('is-scrubbing');
      clearTimeout(scrubIdleT);
      scrubIdleT = setTimeout(() => rotation.classList.remove('is-scrubbing'), 150);
    }
    function pushVelSample(x, t) {
      velSamples.push({ x, t });
      while (velSamples.length > 1 && t - velSamples[0].t > VEL_WINDOW_MS) velSamples.shift();
    }
    function computeReleaseVelocity() {
      if (velSamples.length < 2) return 0;
      const first = velSamples[0], last = velSamples[velSamples.length - 1];
      const dt = last.t - first.t;
      if (dt <= 0) return 0;
      /* rotOffset px/ms = negative of finger px/ms (dragging right shrinks rotOffset). */
      return -((last.x - first.x) / dt);
    }

    /* Global tick index (0..71) currently under the centre indicator. */
    function centredGlobalIdx() {
      const centreX = rotOffset + viewportW() / 2;
      return Math.round((centreX - TICK_WIDTH / 2) / TICK_WIDTH);
    }
    function centredViewIndex() {
      const g = Math.max(0, Math.min(TOTAL_VIEWS * 3 - 1, centredGlobalIdx()));
      return ((g % TOTAL_VIEWS) + TOTAL_VIEWS) % TOTAL_VIEWS;
    }
    function offsetForTick(viewIndex) {
      const g = TOTAL_VIEWS + viewIndex;
      return g * TICK_WIDTH + TICK_WIDTH / 2 - viewportW() / 2;
    }
    /*
      Seamless endless loop: shift rotOffset a whole copy-width when the
      centred tick drifts out of the middle copy (identical copies → no
      visible jump, unlike the old scrollLeft teleport).
    */
    function wrapRotOffset() {
      const copy = Math.floor(centredGlobalIdx() / TOTAL_VIEWS);
      if (copy !== 1) {
        const diff = (copy - 1) * copyWidth();
        rotOffset -= diff;
        if (dragPointerId !== null) dragStartOffset -= diff;
      }
    }
    function applyRot() {
      wrapRotOffset();
      rotTrack.style.transform = `translateX(${-Math.round(rotOffset)}px)`;
      setActiveView(centredViewIndex());
    }
    function stopInertia() {
      if (inertiaRaf) { cancelAnimationFrame(inertiaRaf); inertiaRaf = null; }
    }
    function stopTween() {
      if (tweenRaf) { cancelAnimationFrame(tweenRaf); tweenRaf = null; }
    }
    function tweenTo(target) {
      stopTween();
      const start = rotOffset, dist = target - start;
      if (Math.abs(dist) < 0.5) { rotOffset = target; applyRot(); return; }
      const dur = 260, t0 = performance.now();
      const frame = (now) => {
        const p = Math.min(1, (now - t0) / dur);
        rotOffset = start + dist * (1 - Math.pow(1 - p, 3));
        markScrubbing();
        applyRot();
        tweenRaf = p < 1 ? requestAnimationFrame(frame) : null;
      };
      tweenRaf = requestAnimationFrame(frame);
    }

    rotTrack.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      stopInertia(); stopTween(); /* a fresh touch interrupts any coast/snap */
      dragPointerId   = e.pointerId;
      dragStartX      = e.clientX;
      dragStartOffset = rotOffset;
      dragMoved       = false;
      velSamples.length = 0;
      pushVelSample(e.clientX, performance.now());
      rotation.classList.add('is-dragging');
      markScrubbing();
      /*
        First user touch of the rotation tool — bulk-preload all 24
        mediums in the background so subsequent angles can upgrade.
      */
      startMediumBulkPreload();
      try { rotTrack.setPointerCapture(e.pointerId); } catch (_) { }
    });

    /* One offset write per frame (high-Hz pointer surfaces fire faster than rAF). */
    const flushDrag = () => {
      pendingRaf = null;
      if (pendingOffset === null) return;
      rotOffset = pendingOffset;
      pendingOffset = null;
      markScrubbing();
      applyRot();
    };
    rotTrack.addEventListener('pointermove', (e) => {
      if (dragPointerId !== e.pointerId) return;
      const dx = e.clientX - dragStartX;
      if (!dragMoved && Math.abs(dx) > 3) dragMoved = true;
      if (dragMoved) {
        pendingOffset = dragStartOffset - dx;
        if (!pendingRaf) pendingRaf = requestAnimationFrame(flushDrag);
        pushVelSample(e.clientX, performance.now());
        e.preventDefault();
      }
    });

    function snapToNearestTick() {
      scrollToTickInMiddleCopy(centredViewIndex(), true);
    }

    /*
      Inertial coast: after release we keep scrolling at the user's
      last velocity, decaying it on every frame until it's tiny, then
      snap to the nearest tick. Feels like a turntable spinning to a
      gentle stop instead of slamming on the brakes.

      Tuning:
        DECAY        — multiplier per frame. 0.93 ≈ ~0.5s coast at 60fps.
        STOP_VEL     — when |v| drops below this (px/ms) we hand off to
                       the snap animation.
        MAX_VEL      — clamp absurd flings so a hard flick can't fly past
                       several revolutions of the model.
    */
    function startInertia(v) {
      const DECAY = 0.93, STOP_VEL = 0.03, MAX_VEL = 4.0;
      let vel = Math.max(-MAX_VEL, Math.min(MAX_VEL, v));
      let lastFrame = performance.now();
      const step = (now) => {
        const dt = now - lastFrame;
        lastFrame = now;
        rotOffset += vel * dt;
        markScrubbing();
        applyRot();
        vel *= Math.pow(DECAY, dt / 16.67);
        if (Math.abs(vel) < STOP_VEL) { inertiaRaf = null; snapToNearestTick(); return; }
        inertiaRaf = requestAnimationFrame(step);
      };
      inertiaRaf = requestAnimationFrame(step);
    }

    function endDrag(e) {
      if (dragPointerId !== e.pointerId) return;
      dragPointerId = null;
      rotation.classList.remove('is-dragging');
      try { rotTrack.releasePointerCapture(e.pointerId); } catch (_) { }
      if (!dragMoved) return;
      /*
        Capture the final pointer position so velocity is computed
        through the release moment, not a stale 16ms-old sample.
      */
      pushVelSample(e.clientX, performance.now());
      const v = computeReleaseVelocity();
      if (Math.abs(v) > 0.15) {
        startInertia(v);
      } else {
        /*
          Slow / careful release — go straight to the snap so the bar
          parks on a tick immediately instead of waiting for the
          inertia loop's STOP_VEL threshold.
        */
        snapToNearestTick();
      }
    }
    rotTrack.addEventListener('pointerup', endDrag);
    rotTrack.addEventListener('pointercancel', endDrag);

    /* Suppress click on ticks that fired after a drag. */
    rotTrack.addEventListener('click', (e) => {
      if (dragMoved) { e.stopPropagation(); e.preventDefault(); dragMoved = false; }
    }, true);

    /* Mouse wheel: vertical wheel → bar movement; snap after it stops. */
    rotTrack.addEventListener('wheel', (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        stopInertia(); stopTween();
        rotOffset += e.deltaY;
        markScrubbing();
        applyRot();
        clearTimeout(wheelSnapT);
        wheelSnapT = setTimeout(snapToNearestTick, 180);
        e.preventDefault();
      }
    }, { passive: false });

    /*
      Centre the first tick of the MIDDLE copy. Defer one frame so the
      layout is settled.
    */
    requestAnimationFrame(() => scrollToTickInMiddleCopy(0, false));

    /*
      Centre `viewIndex` of the middle copy under the indicator. The active
      tick + endless wrap are handled in applyRot()/tweenTo(), so there's no
      separate scroll listener.
    */
    function scrollToTickInMiddleCopy(viewIndex, smooth) {
      const target = offsetForTick(viewIndex);
      if (smooth) { tweenTo(target); }
      else { stopTween(); rotOffset = target; applyRot(); }
    }

    /*
      Synchronous, cache-gated swap. The previous implementation awaited
      `Image.decode()` for every swap, which on fast mobile drags caused
      a visible "flicker between two images" — the decode race meant the
      visible <img> would briefly hold an old src while the next was
      still being prepared, and stale resolves landed out of order. Now
      we only swap to URLs that are already in cache (smallLoaded /
      mediumLoaded say yes). Anything else is skipped — the user keeps
      seeing the last cached angle until the new one finishes
      downloading, which feels far better than flickering.
    */
    function swapPreviewSrc(url, fullUrl) {
      if (!previewImg || !url) return;
      if (previewImg.src === url) return;
      /*
        Snapshot the CURRENT visible URL onto the background-fallback
        layer before pointing the <img> at the new URL. On mobile
        browsers the visible <img>'s bitmap can be momentarily empty
        while the browser re-decodes the new URL — even if it's in
        HTTP cache — because decoded-bitmap caches get evicted under
        memory pressure. Without this snapshot, the stage's white
        background flashes through during that decode gap. With it,
        the previous frame stays painted on the bg layer until the
        <img> finishes rendering the new bitmap on top.
      */
      const previousUrl = previewImg.currentSrc || previewImg.src;
      if (bgEl && previousUrl) {
        bgEl.style.backgroundImage = `url("${previousUrl}")`;
      }
      previewImg.src = url;
      previewImg.dataset.imgPreview = url;
      previewImg.dataset.imgFull = fullUrl || url;
    }

    function setActiveView(viewIndex) {
      if (viewIndex < 0 || viewIndex >= TOTAL_VIEWS) return;
      if (viewIndex === currentViewIndex) {
        syncActiveTickHighlight();
        return;
      }
      currentViewIndex = viewIndex;
      rotation.setAttribute('aria-valuenow', String(viewIndex));
      /*
        Resolve to the best-available URL — medium if cached, else
        small if cached, else null. Null = no swap; the user keeps
        seeing the previous cached angle until the new one finishes
        downloading (avoids the broken-icon flash during fast drag).
        ensureMediumForNeighbourhood kicks off background fetches for
        the current + 2 neighbour angles; their onload handlers will
        swap to medium if they finish while the user is still parked
        on the corresponding view.
      */
      const best = pickBestUrlForView(viewIndex);
      if (best) {
        /*
          Keep the slide's outer data-high-url in sync so the magnifier
          (which prefers the slide-level attribute when present) opens
          at the same angle the user is looking at.
        */
        if (slide && best.full) slide.dataset.highUrl = best.full;
        swapPreviewSrc(best.url, best.full);
      }
      ensureMediumForNeighbourhood(viewIndex);
      syncActiveTickHighlight();
    }

    function syncActiveTickHighlight() {
      rotTrack.querySelectorAll('.rotation-bar__tick').forEach(t => {
        const idx = Number(t.dataset.viewIndex);
        t.classList.toggle('is-active', idx === currentViewIndex);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
